import { useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { Avatar, Badge, Button } from '../components/ui';

const NAV: Array<{ label: string; items: Array<{ to: string; label: string; icon: string }> }> = [
  {
    label: 'Overview',
    items: [{ to: '/', label: 'Dashboard', icon: '◧' }],
  },
  {
    label: 'People',
    items: [
      { to: '/users', label: 'All users', icon: '☖' },
      { to: '/admins', label: 'Admin management', icon: '⛬' },
      { to: '/roles', label: 'Roles & permissions', icon: '⚿' },
    ],
  },
  {
    label: 'Security',
    items: [
      { to: '/security', label: 'Security', icon: '⛨' },
      { to: '/login-history', label: 'Login history', icon: '⏻' },
      { to: '/audit', label: 'Audit trail', icon: '⌚' },
    ],
  },
  {
    label: 'Platform',
    items: [
      { to: '/locations', label: 'Locations', icon: '⌘' },
      { to: '/settings', label: 'Settings', icon: '⚙' },
    ],
  },
];

/** The frame of the Master Admin site: its own menu, and nothing of the console's. */
export function MasterShell() {
  const { user, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const items = NAV.flatMap((g) => g.items);
  const title =
    items.find((i) => i.to === location.pathname)?.label ??
    items.find((i) => i.to !== '/' && location.pathname.startsWith(i.to))?.label ??
    'Master Admin';

  return (
    <div className="shell">
      {open && <div className="scrim" onClick={() => setOpen(false)} />}

      <aside className={`sidebar ${open ? 'sidebar--open' : ''}`.trim()}>
        <div className="sidebar__brand">
          <img src="/logo.png" alt="" />
          <div className="sidebar__brand-text">
            <span className="sidebar__brand-name">ISawBazaar</span>
            <span className="sidebar__brand-sub">Master Admin</span>
          </div>
        </div>
        <div className="sidebar__rule" />

        <nav className="sidebar__nav">
          {NAV.map((group) => (
            <div className="nav-group" key={group.label}>
              <div className="nav-group__label">{group.label}</div>
              {group.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/'}
                  className={({ isActive }) => `nav-item ${isActive ? 'nav-item--active' : ''}`}
                  onClick={() => setOpen(false)}
                >
                  <span className="nav-item__icon">{item.icon}</span>
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
      </aside>

      <div className="main">
        <header className="topbar">
          <button className="btn btn--ghost btn--sm menu-toggle" onClick={() => setOpen((v) => !v)} aria-label="Menu">
            ☰
          </button>
          <div>
            <div className="topbar__title">{title}</div>
          </div>
          <div className="topbar__spacer" />
          <div className="row">
            <Badge tone="purple">Master Admin</Badge>
            <Avatar name={user?.displayName ?? 'Master'} />
            <div style={{ lineHeight: 1.25 }}>
              <div style={{ fontSize: 13, fontWeight: 700 }}>{user?.displayName ?? 'Master Admin'}</div>
              <div style={{ fontSize: 11.5, color: 'var(--ink-muted)' }}>{user?.mobile}</div>
            </div>
            <Button size="sm" onClick={signOut}>
              Sign out
            </Button>
          </div>
        </header>

        <main className="content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
